import { createContext, useContext, useEffect, useMemo } from 'react';
import { BrandConfig, brandGround, loadBrandConfig, toRgbChannels } from '../config/brand.config';

const BrandContext = createContext<BrandConfig | null>(null);

const SCALE_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

/** RGB channels for any CSS colour: fast path for hex/rgb, canvas for the rest. */
function colorChannels(color: string): string | null {
  const direct = toRgbChannels(color);
  if (direct) return direct;
  if (typeof CSS === 'undefined' || !CSS.supports('color', color)) return null;
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = color; // the canvas normalises any valid colour to #rrggbb or rgba()
  return toRgbChannels(String(ctx.fillStyle));
}

function applyBrandCssVars(brand: BrandConfig): void {
  const root = document.documentElement;
  const s = brand.primaryScale;

  // Token channels (src/styles/tokens.css) behind every Tailwind `primary-*` /
  // `accent` class. An unparseable value keeps the token default.
  for (const step of SCALE_STEPS) {
    const channels = s[step] ? colorChannels(s[step]) : null;
    if (channels) root.style.setProperty(`--c-brand-${step}`, channels);
  }
  const accentChannels = colorChannels(brand.accentColor);
  if (accentChannels) root.style.setProperty('--c-brand-accent', accentChannels);

  // Brand-tinted ground ("Brand Air"): canvas = surfaceBase (VITE_BRAND_SURFACE_BASE or
  // derived from the scale), subtle and line derived from the scale. Written to the
  // --c-tint-* inputs, never to --c-surface-*, so a dark theme stays untouched.
  const ground = brandGround(s);
  const tints: [string, string][] = [['50', brand.surfaceBase], ['100', ground[100]], ['200', ground[200]]];
  for (const [step, color] of tints) {
    const channels = colorChannels(color);
    if (channels) root.style.setProperty(`--c-tint-${step}`, channels);
  }

  // Legacy hex variables (chartPalette.ts reads --brand-primary-700). --brand-surface-bg
  // and the body colour equal the canvas, so nothing flashes a different ground.
  root.style.setProperty('--brand-primary-50', s[50]);
  root.style.setProperty('--brand-primary-100', s[100]);
  root.style.setProperty('--brand-primary-200', s[200]);
  root.style.setProperty('--brand-primary-300', s[300]);
  root.style.setProperty('--brand-primary-400', s[400]);
  root.style.setProperty('--brand-primary-500', s[500]);
  root.style.setProperty('--brand-primary-600', s[600]);
  root.style.setProperty('--brand-primary-700', s[700]);
  root.style.setProperty('--brand-primary-800', s[800]);
  root.style.setProperty('--brand-primary-900', s[900]);
  root.style.setProperty('--brand-primary-950', s[950]);
  root.style.setProperty('--brand-accent', brand.accentColor);
  root.style.setProperty('--brand-surface-bg', brand.surfaceBase);
  root.style.setProperty('--brand-shadow-tint', brand.shadowTint);
}

/** Points the SVG favicon at the brand's file; the .ico fallback in index.html stays. */
function updateFavicon(faviconUrl: string): void {
  let link = document.querySelector<HTMLLinkElement>("link[rel='icon'][type='image/svg+xml']");
  if (link) {
    if (link.getAttribute('href') !== faviconUrl) link.href = faviconUrl;
  } else {
    link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/svg+xml';
    link.href = faviconUrl;
    document.head.appendChild(link);
  }
}

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const brand = useMemo(() => loadBrandConfig(), []);

  useEffect(() => {
    applyBrandCssVars(brand);
    document.title = brand.browserTitle;
    // The body ground is `rgb(var(--c-canvas))` (index.css), which is surfaceBase via
    // --c-tint-50 in light and the dark canvas in dark; no inline colour here (T-53).
    updateFavicon(brand.faviconUrl);
    // <meta name="theme-color"> is owned by ThemeProvider (brand.themeColor in light).
  }, [brand]);

  return (
    <BrandContext.Provider value={brand}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand(): BrandConfig {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used within BrandProvider');
  return ctx;
}
